import { Form } from 'factorial-form'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import { durationInSeconds } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

interface ValidationProps {
  startAt?: moment.Moment
  endAt?: moment.Moment
  duration?: number
}

export const BREAK_TYPE = Object.freeze({
  fixed: 'fixed',
  flexible: 'flexible',
  semiFlexible: 'semi_flexible',
})

class PlannedBreakForm extends Form {
  constructor(values: Record<string, unknown> = {}, isTimeFormat24H: boolean) {
    super(
      { ...values, isTimeFormat24H },
      {
        id: 'string',
        isTimeFormat24H: 'boolean',
        break_type: 'string',
        start_at: 'string',
        end_at: 'string',
        duration: 'number',
        break_configuration: {
          id: 'string',
          name: 'string',
          paid: 'boolean',
          archived: 'boolean',
        },
      }
    )
    makeObservable(this, {
      id: computed,
      isArchived: computed,
      isFlexible: computed,
      isFixed: computed,
      isSemiFlexible: computed,
      isPaid: computed,
      startAt: computed,
      startAtMoment: computed,
      endAt: computed,
      endAtMoment: computed,
      isOvernight: computed,
      formattedStartAt: computed,
      formattedEndAt: computed,
      duration: computed,
      durationInSeconds: computed,
    })
  }

  get id(): string | null {
    return this.get('id').value || null
  }

  get breakType(): string {
    return this.get('break_type').value
  }

  get isArchived(): boolean {
    return this.get('break_configuration.archived').value
  }

  get isFlexible(): boolean {
    return this.breakType === BREAK_TYPE.flexible
  }

  get isFixed(): boolean {
    return this.breakType === BREAK_TYPE.fixed
  }

  get isSemiFlexible(): boolean {
    return this.breakType === BREAK_TYPE.semiFlexible
  }

  get isPaid(): boolean {
    return this.get('break_configuration.paid').value
  }

  get duration(): number {
    return this.get('duration').value
  }
  get startAt(): string {
    return this.get('start_at').value
  }

  get endAt(): string {
    return this.get('end_at').value
  }

  get isOvernight() {
    return this.startAt > this.endAt
  }

  get timeFormatIs24H(): boolean {
    return this.get('isTimeFormat24H').value
  }

  get startAtMoment(): moment.Moment {
    return moment(this.startAt, 'HH:mm')
  }

  get endAtMoment(): moment.Moment {
    return moment(this.endAt, 'HH:mm')
  }

  get formattedStartAt(): string {
    return dateToString(this.startAtMoment, 'ui_short_with_time')
  }

  get formattedEndAt(): string {
    const endAt = this.isOvernight ? this.endAtMoment.clone().add('1', 'day') : this.endAtMoment
    return dateToString(endAt, 'ui_short_with_time')
  }

  isValid({ startAt, endAt, duration }: ValidationProps): boolean {
    return (
      this.isValidSemiFlexibleDuration({ duration }) ||
      this.isValidFlexible({ startAt, endAt, duration }) ||
      this.isValidFixed({ startAt, endAt }) ||
      this.isValidSemiFlexible({ startAt, endAt, duration })
    )
  }

  validationError({ startAt, endAt, duration }: ValidationProps): string {
    if (this.isFlexible && !this.isValidFlexible({ startAt, endAt, duration })) {
      return i18n.t('shift_management.planned_breaks.errors.bigger_range')
    }

    if (
      this.isFixed &&
      !this.isValidFixed({ startAt, endAt }) &&
      !this.isValidSemiFlexibleDuration({ duration }) // Fixed breaks with flexible work schedule
    ) {
      return i18n.t('shift_management.planned_breaks.errors.equal_frame')
    }

    if (
      this.isSemiFlexible &&
      !this.isValidSemiFlexibleDuration({ duration }) &&
      !this.isValidSemiFlexible({ startAt, endAt, duration })
    ) {
      return i18n.t('shift_management.planned_breaks.errors.outside_of_range')
    }

    return ''
  }

  validate({ startAt, endAt, duration }: ValidationProps): boolean {
    this.resetErrors()
    const error = this.validationError({ startAt, endAt, duration })

    if (!error) return true
    if (this.isFlexible) this.get('duration').setErrors([error])
    if (this.isFixed || this.isSemiFlexible) this.get('start_at').setErrors([error])

    return !this.hasErrors
  }

  // Flexible schedule with semi flexible break
  isValidSemiFlexibleDuration({ duration }: ValidationProps): boolean {
    if (!duration) return false
    // We first check if the range is smaller than schedule's duration
    // And then if the break duration is valid
    return this.rangeDuration <= duration && this.isValidDuration(duration)
  }

  // Flexible break type or flexible schedule
  isValidFlexible({ startAt, endAt, duration }: ValidationProps): boolean {
    if (!duration && !this.isFlexible) return false

    // if we have duration, then its a flexible schedule, we need to compare duration vs break's duration
    const shiftDuration = this.calculateShiftDuration({ startAt, endAt, duration })
    return !!shiftDuration && this.isValidDuration(shiftDuration)
  }

  // Fixed break type
  isValidFixed({ startAt, endAt }: ValidationProps): boolean {
    return !!startAt && !!endAt && this.isFixed && this.isValidTime(startAt, endAt)
  }

  // Semiflexible break type
  isValidSemiFlexible({ startAt, endAt, duration }: ValidationProps): boolean {
    const shiftDuration = this.calculateShiftDuration({ startAt, endAt, duration })

    return (
      !!shiftDuration &&
      !!startAt &&
      !!endAt &&
      this.isValidDuration(shiftDuration) &&
      this.isValidTime(startAt, endAt)
    )
  }

  calculateShiftDuration({ startAt, endAt, duration }: ValidationProps): number | undefined {
    return startAt && endAt
      ? durationInSeconds(startAt.format('HH:mm'), endAt.format('HH:mm'), this.timeFormatIs24H)
      : duration
  }

  isValidTime(shiftStartAt: moment.Moment, shiftEndAt: moment.Moment): boolean {
    const isNightShift = shiftStartAt > shiftEndAt
    const shiftEndAtNextDay = isNightShift ? shiftEndAt.clone().add(1, 'day') : shiftEndAt

    const isOverMidnightBreak = this.startAtMoment > this.endAtMoment // 10pm - 1am
    const isWithinNightBreak =
      this.startAtMoment < shiftStartAt && this.startAtMoment < this.endAtMoment // 1am - 2am

    let startAtMoment = this.startAtMoment
    let endAtMoment = this.endAtMoment

    // check full break passed midnight - we have to add 1 day to start and end
    if (isWithinNightBreak) startAtMoment = this.startAtMoment.clone().add(1, 'day')
    // check end time midnight crossing
    if (isOverMidnightBreak || isWithinNightBreak)
      endAtMoment = this.endAtMoment.clone().add(1, 'day')

    const regEx = new RegExp(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/)

    const isValidStartTime = regEx.test(this.startAt) && startAtMoment >= shiftStartAt
    const isValidEndTime = regEx.test(this.endAt) && endAtMoment <= shiftEndAtNextDay

    return isValidEndTime && isValidStartTime
  }

  isValidDuration(shiftDuration: number): boolean {
    if (!this.durationInSeconds) return false

    return this.isConsistent && this.durationInSeconds <= shiftDuration
  }

  get isConsistent() {
    if (!this.isSemiFlexible) return true

    // Semiflexible case:  we need to check if the duration is inside the break time frame
    return !!this.startAt && !!this.endAt && this.rangeDuration >= this.durationInSeconds
  }

  get durationInSeconds(): number {
    // Covers the Fixed type
    if (this.isFixed) {
      return this.rangeDuration
    }

    return this.duration * 60
  }

  get rangeDuration(): number {
    return durationInSeconds(this.startAt, this.endAt, this.timeFormatIs24H)
  }

  clone(): PlannedBreakForm {
    return new PlannedBreakForm({ ...this.serialized, id: null }, this.timeFormatIs24H)
  }
}

export default PlannedBreakForm
